import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import * as moment from 'moment';

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { fiFI, ruRU, svSE } from '@mui/x-date-pickers/locales';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import { withStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { getDefaultLanguageCode } from '../../utils/localeUtils';

const styles = () => ({
  root: {},
  calendar: {
    marginBottom: '25px',
    marginTop: '5px',
  },
});

const ServerDay = props => {
  // eslint-disable-next-line react/prop-types
  const { availability = [], day, outsideCurrentMonth, classes, ...other } = props;

  const hasAvailability =
    // eslint-disable-next-line react/prop-types
    availability.length > 0 && !outsideCurrentMonth && availability.includes(day.format('YYYY-MM-DD'));

  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      style={{ backgroundColor: hasAvailability ? '#E2F4E4' : '', border: hasAvailability ? '2px solid #207028' : '' }}
    />
  );
};

const Calendar = ({
  classes,
  className,
  label,
  disabled,
  value,
  required,
  lang,
  availableTimes,
  loading,
  ...otherProps
}) => {
  const locale = lang || getDefaultLanguageCode();

  moment.locale(locale);

  const today = moment();

  const availability =
    // eslint-disable-next-line react/prop-types
    availableTimes && availableTimes.map(item => item.attributes.timeSlots.length > 0 && item.attributes.date);

  let calendarLocale = '';
  switch (lang) {
    case 'fi':
      calendarLocale = fiFI;
      break;
    case 'sv':
      calendarLocale = svSE;
      break;
    case 'ru':
      calendarLocale = ruRU;
      break;
  }

  return (
    <>
      <InputLabel shrink style={{ textAlign: 'left', fontSize: '20px', color: '#000' }}>
        {label} {required && '*'}
      </InputLabel>
      <Box sx={{ position: 'relative' }}>
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={locale}
          localeText={get(calendarLocale, ['components', 'MuiLocalizationProvider', 'defaultProps', 'localeText'])}>
          <DateCalendar
            {...otherProps}
            className={classes.calendar}
            disabled={disabled}
            disablePast
            maxDate={today.add(1, 'years')}
            renderLoading={() => <DayCalendarSkeleton />}
            required={required}
            slots={{
              day: ServerDay,
            }}
            slotProps={{
              day: {
                availability,
              },
            }}
            views={['day']}
          />
        </LocalizationProvider>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              bgcolor: 'rgba(0,0,0,0.26)',
              top: '0',
              bottom: '0',
              left: '0',
              right: '0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <CircularProgress size={80} />
          </Box>
        )}
      </Box>
    </>
  );
};

Calendar.propTypes = {
  availableTimes: PropTypes.array,
  classes: PropTypes.object,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  lang: PropTypes.string,
  required: PropTypes.bool,
  loading: PropTypes.bool,
  value: PropTypes.any,
};

Calendar.defaultProps = {
  availableTimes: [],
  classes: {},
  className: '',
  disabled: false,
  label: '',
  lang: '',
  required: false,
  value: null,
  loading: false,
};

export default withStyles(styles)(Calendar);
